import React from "react";

export default function CreateJobApiDoc() {
  return (
    <div className="text-gray-800 text-md leading-7 px-4 min-w-0 max-w-[40rem] lg:mt-0 lg:mr-0 lg:max-w-[50rem]">
      {/* Header */}
      <header className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-transparent" />
        <div className="relative p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Jobs API — Create Job
          </h1>
          <p className="mt-3 max-w-3xl text-base sm:text-lg leading-7 text-slate-600">
            Create a new job from Portside using{" "}
            <span className="font-semibold">multipart/form-data</span>. This
            guide is tailored for Portside customer users and includes only the
            fields and behaviors relevant to that integration.
          </p>

          {/* Quick facts */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Method
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                POST (create)
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Auth
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">JWT</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Endpoint
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                /api/jobs/create/
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
        {/* Overview */}
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Overview
          </h2>
          <p className="mt-3 leading-7 text-slate-700">
            Creates a job and returns the new job ID and purchase order. For
            Portside users, the API is designed to be flexible when matching
            operational data:
          </p>
          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              You must provide a{" "}
              <code className="font-mono">customer_name</code> so the job can be
              associated with the correct integration customer.
            </li>
            <li>
              For <span className="font-semibold">aircraft type</span>,{" "}
              <span className="font-semibold">airport</span>, and{" "}
              <span className="font-semibold">FBO</span>, you may pass either an
              Livetakeoff internal ID or the name of the entry (airport, FBO,
              etc.). We would do a best-effort match on the name, so you don't
              have to worry about keeping up with internal IDs. In the case of a
              mismatch, the system can use placeholder entries and preserve the
              mismatch details in comments for later review, so the integration
              can keep moving without perfect data.
            </li>
            <li>
              Dates must be browser-style strings. Booleans are sent as strings:{" "}
              <code className="font-mono">"true"</code> /{" "}
              <code className="font-mono">"false"</code>.
            </li>
            <li>
              Send the literal string <code className="font-mono">"null"</code>{" "}
              to omit an optional date.
            </li>
          </ul>
        </section>

        {/* Portside-specific behavior */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Portside-specific behavior
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <ul className="grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
              <li>
                If the provided <code className="font-mono">customer_name</code>{" "}
                does not match an existing customer, the system can temporarily
                route the job to a placeholder customer and add an internal
                fallback comment for operations follow-up.
              </li>
              <li>
                If <code className="font-mono">aircraft_type_name</code>,{" "}
                <code className="font-mono">airport_code</code>, or{" "}
                <code className="font-mono">fbo_name</code> cannot be matched,
                the system can use placeholder entries and preserve the mismatch
                details in comments for later review.
              </li>
              <li>
                The endpoint still returns{" "}
                <code className="font-mono">201 Created</code> as long as job
                creation succeeds, even if one or more placeholders had to be
                used.
              </li>
            </ul>
          </div>

          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <div className="text-sm font-semibold text-amber-900">
              Important note
            </div>
            <p className="mt-2 text-sm leading-6 text-amber-900">
              Portside should prefer sending real matched values whenever
              possible. Placeholder handling exists to keep the integration
              moving, but exact matches produce cleaner downstream reporting and
              operations workflows.
            </p>
          </div>
        </section>

        {/* Request */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Request
          </h2>

          <div className="mt-4">
            {/* HTTP */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">HTTP</div>
              <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
                <li>
                  <span className="font-semibold">Method:</span>{" "}
                  <code className="font-mono">POST</code>
                </li>
                <li>
                  <span className="font-semibold">URL:</span>{" "}
                  <code className="font-mono">/api/jobs/create/</code>
                </li>
                <li>
                  <span className="font-semibold">Headers:</span>
                  <br />
                  <code className="font-mono">
                    Authorization: JWT your_token
                  </code>
                  <br />
                  <code className="font-mono">
                    Content-Type: multipart/form-data
                  </code>
                </li>
              </ul>
            </div>

            {/* Body schema */}
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Body fields (multipart/form-data)
              </div>
              <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-700">
                <li>
                  <code className="font-mono">tailNumber</code> —{" "}
                  <span className="font-mono">string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    required
                  </span>{" "}
                  Aircraft tail number.
                </li>

                <li>
                  <code className="font-mono">customer_name</code> —{" "}
                  <span className="font-mono">string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    required for Portside
                  </span>{" "}
                  Used to associate the job with the correct integration
                  customer.
                </li>

                <li>
                  <code className="font-mono">aircraft_type_id</code> —{" "}
                  <span className="font-mono">number|string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    conditional
                  </span>{" "}
                  Use this if you know the internal aircraft type ID.
                </li>
                <li>
                  <code className="font-mono">aircraft_type_name</code> —{" "}
                  <span className="font-mono">string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    conditional
                  </span>{" "}
                  Use this if you do not know the internal ID.
                </li>
                <li>
                  Either <code className="font-mono">aircraft_type_id</code> or{" "}
                  <code className="font-mono">aircraft_type_name</code> is
                  required. If both are provided, the system prioritizes the ID.
                </li>

                <li>
                  <code className="font-mono">airport_id</code> —{" "}
                  <span className="font-mono">number|string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    conditional
                  </span>{" "}
                  Internal airport ID.
                </li>
                <li>
                  <code className="font-mono">airport_code</code> —{" "}
                  <span className="font-mono">string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    conditional
                  </span>{" "}
                  Airport code / initials such as{" "}
                  <code className="font-mono">FXE</code>,{" "}
                  <code className="font-mono">OPF</code>,{" "}
                  <code className="font-mono">FLL</code>.
                </li>

                <li>
                  Either <code className="font-mono">airport_id</code> or{" "}
                  <code className="font-mono">airport_code</code> is required.
                  If both are provided, the system prioritizes the ID.
                </li>

                <li>
                  <code className="font-mono">fbo_id</code> —{" "}
                  <span className="font-mono">number|string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    conditional
                  </span>{" "}
                  Internal FBO ID.
                </li>
                <li>
                  <code className="font-mono">fbo_name</code> —{" "}
                  <span className="font-mono">string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    conditional
                  </span>{" "}
                  Exact or near-operational FBO name.
                </li>

                <li>
                  Either <code className="font-mono">fbo_id</code> or{" "}
                  <code className="font-mono">fbo_name</code> is required. If
                  both are provided, the system prioritizes the ID.
                </li>

                <li>
                  <code className="font-mono">services</code> —{" "}
                  <span className="font-mono">string</span> (comma-separated
                  internal service IDs)
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    required
                  </span>
                </li>

                <li>
                  <code className="font-mono">on_site</code> —{" "}
                  <span className="font-mono">"true"|"false"</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>
                </li>

                <li>
                  <code className="font-mono">estimated_arrival_date</code> —{" "}
                  <span className="font-mono">string</span> (browser-style date)
                  or <span className="font-mono">"null"</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>
                </li>

                <li>
                  <code className="font-mono">estimated_departure_date</code> —{" "}
                  <span className="font-mono">string</span> (browser-style date)
                  or <span className="font-mono">"null"</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>
                </li>

                <li>
                  <code className="font-mono">complete_by_date</code> —{" "}
                  <span className="font-mono">string</span> (browser-style date)
                  or <span className="font-mono">"null"</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>
                </li>

                <li>
                  <code className="font-mono">requested_by</code> —{" "}
                  <span className="font-mono">string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    required
                  </span>
                </li>
                <li>
                  Requested_by refers to the dispatcher's information in the
                  following format: "first name - last name - email". For
                  example, "John - Doe - john.doe@example.com". This helps the
                  operations team identify the source of the request and contact
                  the dispatcher if needed.
                </li>

                <li>
                  <code className="font-mono">customer_purchase_order</code> —{" "}
                  <span className="font-mono">string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>
                </li>

                <li>
                  <code className="font-mono">priority</code> —{" "}
                  <span className="font-mono">"H"|"N"</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>
                </li>

                <li>
                  <code className="font-mono">comment</code> —{" "}
                  <span className="font-mono">string</span>
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>
                </li>

                <li>
                  <code className="font-mono">image</code> —{" "}
                  <span className="font-mono">file</span> (repeatable; send
                  multiple <code className="font-mono">image</code> parts)
                  <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-xs">
                    optional
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Example: cURL (Portside) */}
          <div className="mt-6 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  POST
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
              <span className="font-mono text-xs text-zinc-400">
                /api/jobs/create/
              </span>
            </div>
            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`curl -X POST https://api.livetakeoff.com/api/jobs/create/ \\
  -H "Authorization: JWT YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: multipart/form-data" \\
  -F "tailNumber=N123AB" \\
  -F "customer_name=Portside Demo Customer" \\
  -F "aircraft_type_name=Gulfstream G550" \\
  -F "airport_code=FXE" \\
  -F "fbo_name=Signature Flight Support" \\
  -F "estimated_arrival_date=Tue Jan 21 2025 14:00:00 GMT-0500 (Eastern Standard Time)" \\
  -F "estimated_departure_date=null" \\
  -F "complete_by_date=null" \\
  -F "services=3,8,15" \\
  -F "priority=N" \\
  -F "customer_purchase_order=PO-8891" \\
  -F "requested_by=John - Doe - john.doe@example.com" \\
  -F "comment=Please prioritize cabin detail" \\
  -F "image=@/path/photo1.jpg"`}
                </pre>
              </div>
            </div>
          </div>

          {/* Example: fetch + FormData */}
          <div className="mt-6 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>
            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  POST
                </span>
              </div>
              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
              <span className="font-mono text-xs text-zinc-400">
                /api/jobs/create/
              </span>
            </div>
            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  {`const fd = new FormData();
fd.append("tailNumber", "N123AB");
fd.append("customer_name", "Portside Demo Customer");

// You may send either IDs or names/codes:
fd.append("aircraft_type_name", "Gulfstream G550");
fd.append("airport_code", "FXE");
fd.append("fbo_name", "Signature Flight Support");

// Dates must be browser-style strings or "null"
fd.append("estimated_arrival_date", new Date().toString());
fd.append("estimated_departure_date", "null");
fd.append("complete_by_date", "null");

fd.append("services", "3,8,15");
fd.append("priority", "N");
fd.append("requested_by", "John - Doe - john.doe@example.com");
fd.append("customer_purchase_order", "PO-8891");
fd.append("comment", "Please prioritize cabin detail");

// Optional files (repeat "image")
for (const file of selectedFiles) fd.append("image", file);

const res = await fetch("https://api.livetakeoff.com/api/jobs/create/", {
  method: "POST",
  headers: { Authorization: "JWT " + token },
  body: fd
});

if (!res.ok) throw new Error("Failed to create job");
const data = await res.json(); // => { id, purchase_order }`}
                </pre>
              </div>
            </div>
          </div>
        </section>

        {/* Matching rules */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Matching rules
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <ul className="grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
              <li>
                For aircraft type, send either{" "}
                <code className="font-mono">aircraft_type_id</code> or{" "}
                <code className="font-mono">aircraft_type_name</code>.
              </li>
              <li>
                For airport, send either{" "}
                <code className="font-mono">airport_id</code> or{" "}
                <code className="font-mono">airport_code</code>.
              </li>
              <li>
                For FBO, send either <code className="font-mono">fbo_id</code>{" "}
                or <code className="font-mono">fbo_name</code>.
              </li>
              <li>
                If both ID and name/code are provided for the same resource, the
                endpoint prioritizes the internal ID.
              </li>
            </ul>
          </div>
        </section>

        {/* Response */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Response
          </h2>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">
              201 Created
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-700">
              On success, returns a minimal payload containing the new job ID
              and purchase order.
            </p>
            <div className="mt-4 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Response
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    JSON
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`{
  "id": 9876,
  "purchase_order": "20260122-4"
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>

          {/* Errors */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-semibold text-slate-900">Errors</div>
            <ul className="mt-2 space-y-1 text-sm leading-6 text-slate-700">
              <li>
                <span className="font-mono">403</span> — insufficient privileges
                to create a job.
              </li>
              <li>
                <span className="font-mono">400</span> — missing or invalid
                required fields.
              </li>
              <li>
                <span className="font-mono">500</span> — server-side creation
                failure.
              </li>
            </ul>

            <div className="mt-4 not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
              <div className="flex min-h-[calc(theme(spacing.12)+1px)] flex-wrap items-start gap-x-4 border-b border-zinc-700 bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent">
                <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                  Example 400
                </h3>
              </div>
              <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
                <div className="dark flex">
                  <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                    JSON
                  </span>
                </div>
                <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>
                <span className="font-mono text-xs text-zinc-400">
                  application/json
                </span>
              </div>
              <div className="group dark:bg-white/2.5 pb-3">
                <div className="relative">
                  <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                    {`{
  "error": "customer_name is required for Portside Integration users"
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Validation / Portside rules */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Validation & Portside rules
          </h2>
          <ul className="mt-3 grid list-disc gap-2 pl-5 text-slate-700 marker:text-slate-400">
            <li>
              Always use <code className="font-mono">multipart/form-data</code>.
              The endpoint does <span className="italic">not</span> accept raw
              JSON.
            </li>
            <li>
              Dates must be browser-style strings such as{" "}
              <code className="font-mono">
                Tue Jan 21 2025 14:00:00 GMT-0500 (Eastern Standard Time)
              </code>{" "}
              or the literal string <code className="font-mono">"null"</code>.
            </li>
            <li>
              Boolean fields are strings:{" "}
              <code className="font-mono">"true"</code> /{" "}
              <code className="font-mono">"false"</code>.
            </li>
            <li>
              For multiple files, repeat the{" "}
              <code className="font-mono">image</code> field for each file.
            </li>
            <li>
              <code className="font-mono">services</code> must be sent as a
              comma-separated list of internal service IDs.
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}
